import { StrictMode } from "react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { ConsentBanner } from "@rook/landing/features/consent/ConsentBanner";
import { RootErrorBoundary } from "@rook/shared/components/RootErrorBoundary";
import { TooltipProvider } from "@rook/ui";


import { App } from "./App";

import "@rook/shared/styles/globals.css";

/**
 * Entry point for `@rook/portal` — the per-env per-tenant application.
 * Deploys to `<env>.clients.portal.tryrook.io` in the env's account.
 *
 * Provider order mirrors `@rook/landing` and the original root SPA's
 * `src/main.tsx` 1:1 — keeping the cross-package contract on session
 * state, query dedupe, and error reporting predictable:
 *  - <RootErrorBoundary>   uncaught render/effect errors → /api/observability/error
 *  - <BrowserRouter>       react-router v6 history
 *  - <QueryClientProvider> TanStack Query (server-state)
 *  - <TooltipProvider>     Radix tooltip context with 300 ms delay
 *  - <StrictMode>          React 19 double-invoke catch (dev)
 *
 * `<AuthProvider>` is intentionally inside `<App>` (not here) so
 * it has router context — the auth flow needs `useNavigate()` for
 * session-expired bounces back to the landing.
 *
 * `<ConsentBanner>` lives at the body root (outside any route) so
 * it's visible on every page. Cross-imported from `@rook/landing`
 * because cookie consent is the same legal text across both apps;
 * duplicating the component would create silent drift the first
 * time legal asks for a wording tweak. The import does NOT make
 * landing-features available — only this one named export crosses.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

const container = document.getElementById("root");
if (!container) {
  throw new Error("@rook/portal: missing #root element in index.html");
}

createRoot(container).render(
  <StrictMode>
    <RootErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider delayDuration={300}>
            <App />
            <ConsentBanner />
          </TooltipProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </RootErrorBoundary>
  </StrictMode>,
);
